import { useContext, useEffect, useRef, useState } from "react";
import "../styles/AnalizeSession.css";
import { useNavigate, useParams } from "react-router-dom";
import { Context } from "../services/Context";
import Swal from "sweetalert2";
import axios from "axios";
import Routes from "../connection/path";
import { SessionHeader, AnalysisWorkspace } from "../components/organisms";
import { Spinner } from "../components/atoms";
import {
  computeMetrics,
  computeTotal,
  computeAverage,
  computeStandardDeviation,
  computeCorrectPercentage,
  computeErrorPercentage,
  computeSessionCompleteness,
} from "../utility/analizeSession/metrics";
import {
  applyDecisionMark,
  applyArrivalMark,
  resetRows,
  serializeMoves,
} from "../utility/analizeSession/sessionMoves";

const AnalizeSession = () => {
  const {
    urlVision,
    videoCurrentSession,
    isSaveCurrentSession,
    listOfPlayers,
    infoSession,
    currentFPS,
    CrudApi,
    currentSession,
    userContext,
    showSessionType,
    currentCalibration,
    translate,
  } = useContext(Context);
  const navigate = useNavigate();
  const session = useParams().session;
  const FPS = useRef(currentFPS.current ? currentFPS.current : 29.97);
  const currentPlayer = useRef(null);
  const [videoSession, setVideoSession] = useState(null);
  const [videoState, setVideoState] = useState("Play");
  const [currentFrame, setCurrentFrame] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);
  const [loading, setLoading] = useState(true);
  const [imagePlay, setImagePlay] = useState({
    currentPlay: null,
    prevPlay: null,
  });
  const [currentStimulus, setCurrentStimulus] = useState(0);
  const [selectedRowIndex, setSelectedRowIndex] = useState(0);
  const selectedPlayID = useRef(null);
  const [playingVideo, setPlayingVideo] = useState(false);
  const videoRefs = useRef([]);
  const blobUrlsRef = useRef(new Set());
  const [processing, setProcessing] = useState({
    value: 1,
    message: "Procesar Pasos",
  });
  const [saveStatus, setSaveStatus] = useState("idle");
  const [metrics, setMetrics] = useState(null);
  const [viewMode, setViewMode] = useState("horizontal");

  const findPlayBySequence = (sequence) => {
    const playsFromDb = infoSession.current?.playsFromDb;
    if (!Array.isArray(playsFromDb)) return null;
    const seq = parseInt(sequence, 10);
    if (Number.isNaN(seq)) return null;
    return (
      playsFromDb.find((play) => parseInt(play.playName, 10) === seq) || null
    );
  };

  const loadPlaysFromDb = async () => {
    try {
      const response = await CrudApi.get(Routes.playsRoutes.GETPLAYFIGCOORD);
      return Array.isArray(response)
        ? response.filter((play) => play.enable)
        : [];
    } catch (error) {
      console.error("Error al obtener jugadas:", error);
      return [];
    }
  };

  const initialData = Array.from({ length: 0 }, (_, index) => ({
    sequence: index + 1,
    playID: index + 1,
    error: false,
    estimulo: 0,
    decisionMaking: 0,
    arrival: 0,
    visuMotor: 0,
    motor: 0,
    cognitiveMotor: 0,
    autoComplete: false,
  }));

  const [tableData, setTableData] = useState(initialData);

  const PLACEHOLDER_VIDEO = process.env.PUBLIC_URL
    ? `${process.env.PUBLIC_URL}/mcp-placeholder-video.mp4`
    : "/mcp-placeholder-video.mp4";
  const PLACEHOLDER_IMAGE = process.env.PUBLIC_URL
    ? `${process.env.PUBLIC_URL}/mcp-placeholder-image.jpg`
    : "/mcp-placeholder-image.jpg";

  const getApiBaseUrl = () =>
    (process.env.REACT_APP_APIMCP || "https://www.spt-group.cl/api").replace(
      /\/+$/,
      ""
    );

  const buildVideoStreamUrl = (fileName) => {
    if (!fileName || typeof fileName !== "string") return null;
    if (/^(blob:|https?:\/\/)/.test(fileName)) return fileName;
    return `${getApiBaseUrl()}/public/minio/video/${fileName}`;
  };

  const getSessionVideoURL = (session) =>
    session?.videoURL || session?.videoUrl || null;

  const safeSeekTo = (ref, time, unit) => {
    try {
      ref?.seekTo?.(time, unit);
    } catch (_) {}
  };

  const safeGetCurrentTime = (ref) => {
    try {
      return typeof ref?.getCurrentTime === "function"
        ? ref.getCurrentTime()
        : 0;
    } catch (_) {
      return 0;
    }
  };

  const registerBlobUrl = (url) => {
    if (url && url.startsWith("blob:")) {
      blobUrlsRef.current.add(url);
    }
    return url;
  };

  const revokeBlobUrls = () => {
    blobUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch (_) {}
    });
    blobUrlsRef.current.clear();
  };

  const fetchBlobUrl = async (path, type = "image") => {
    const placeholder =
      type === "video" ? PLACEHOLDER_VIDEO : PLACEHOLDER_IMAGE;
    try {
      const blob = await CrudApi.getBlob(`minio/${type}/${path}`);
      if (!blob || blob.size === 0) {
        console.warn(
          `[fetchBlobUrl] Blob vacío para ${type} "${path}", usando placeholder`
        );
        return placeholder;
      }
      return registerBlobUrl(URL.createObjectURL(blob));
    } catch (error) {
      console.warn(
        `[fetchBlobUrl] Error al obtener ${type} "minio/${type}/${path}" (${
          error?.response?.status || error?.message || "network error"
        }), usando placeholder`
      );
      return placeholder;
    }
  };


  const handleCheckboxChange = (index) => {
    setTableData((prev) =>
      prev.map((row, i) => (i === index ? { ...row, error: !row.error } : row))
    );
  };

  const getMinioVideo = async () => {
    let videoUrl = null;
    try {
      const session = currentSession.current?.[0];
      const fileName = getSessionVideoURL(session);
      videoUrl = buildVideoStreamUrl(fileName);

      if (!videoUrl) {
        console.warn("[getMinioVideo] No se encontró videoURL, usando placeholder");
        setVideoSession(PLACEHOLDER_VIDEO);
        return;
      }

      if (session?.duration && session.fps > 0) {
        const durationFromMetadata = session.duration / session.fps;
        if (durationFromMetadata > 0) setVideoDuration(durationFromMetadata);
      }

      const maxStimulusMs = Math.max(
        0,
        ...(session?.SessionMoves || []).map((m) => Number(m.stimulus) || 0),
        ...(infoSession.current?.stimulusTime || []).map((s) => Number(s) || 0)
      );
      if (maxStimulusMs > 0) {
        const minDurationFromStimulus = (maxStimulusMs + 2000) / 1000;
        setVideoDuration((prev) => Math.max(prev || 0, minDurationFromStimulus));
      }

      setVideoSession(videoUrl);

      const imageSequences = infoSession.current?.imageSequences;
      if (imageSequences?.length) {
        setImagePlay({
          currentPlay: imageSequences[0],
          prevPlay: imageSequences[0],
        });
      }

      if (infoSession.current && session?.SessionMoves) {
        infoSession.current.stimulusTime.sort((a, b) => a - b);
        session.SessionMoves.sort((a, b) => a.stimulus - b.stimulus);

        setTableData(
          Array.from(
            { length: infoSession.current.sequenceOfPlays.length },
            (_, index) => ({
              sequence: index + 1,
              playID: infoSession.current.sequenceOfPlays[index],
              error: session.SessionMoves[index].error,
              estimulo: session.SessionMoves[index].stimulus,
              decisionMaking: session.SessionMoves[index].decisionMaking,
              arrival: session.SessionMoves[index].arrival,
              visuMotor: session.SessionMoves[index].presentedMs,
              motor: session.SessionMoves[index].motor,
              cognitiveMotor: session.SessionMoves[index].cognitiveMotor,
              autoComplete: session.SessionMoves[index].autoComplete,
            })
          )
        );
      }
    } catch (error) {
      console.error("Error al cargar el video desde MinIO:", error);
      if (!videoUrl) setVideoSession(PLACEHOLDER_VIDEO);
    }
  };


  useEffect(() => {
    const loadingTimeout = setTimeout(() => setLoading(false), 8000);

    const initSession = async () => {
      try {
        videoRefs.current = Array(5)
          .fill()
          .map((_, index) => videoRefs.current[index]);

        safeSeekTo(videoRefs.current[0], -2 / FPS.current, "seconds");
        safeSeekTo(videoRefs.current[1], -1 / FPS.current, "seconds");
        safeSeekTo(videoRefs.current[2], 0 / FPS.current, "seconds");
        safeSeekTo(videoRefs.current[3], 1 / FPS.current, "seconds");
        safeSeekTo(videoRefs.current[4], 2 / FPS.current, "seconds");

        if (session === "current" && videoCurrentSession.current) {
          const url = registerBlobUrl(
            URL.createObjectURL(videoCurrentSession.current)
          );
          currentPlayer.current = listOfPlayers.current.find(
            (player) => player.playerId === infoSession.current.playerSelected
          );
          setVideoSession(url);
          setImagePlay({
            currentPlay: infoSession.current.imageSequences[0],
            prevPlay: infoSession.current.imageSequences[0],
          });

          setTableData(
            Array.from(
              { length: infoSession.current.sequenceOfPlays.length },
              (element, index) => ({
                sequence: index + 1,
                playID: infoSession.current.sequenceOfPlays[index],
                error: false,
                estimulo: infoSession.current.stimulusTime[index],
                decisionMaking: 0,
                arrival: 0,
                visuMotor: 0,
                motor: 0,
                cognitiveMotor: 0,
                autoComplete: false,
              })
            )
          );
        } else {
          if (currentSession.current === null) {
            try {
              const endpoint = `sessions/${session}`;
              const [sessionData, analyticData] = await Promise.all([
                CrudApi.get(endpoint),
                CrudApi.get(`${endpoint}/sessionMoves-Analytic`),
              ]);
              const sessionRow = {
                ...(sessionData || {}),
                SessionMoves:
                  analyticData?.SessionMoves ||
                  sessionData?.SessionMoves ||
                  [],
              };
              const player = sessionRow.playerId
                ? await CrudApi.get(`player/${sessionRow.playerId}`)
                : null;
              currentSession.current = [sessionRow, player];
            } catch (error) {
              console.error(
                "[initSession] No se pudo cargar la sesión por enlace directo:",
                error
              );
              navigate("/other-sessions");
              return;
            }
          }

          FPS.current = currentSession.current[0].fps ?? 30;

          const stimulusTime = currentSession.current[0].SessionMoves.map(
            (move) => move.stimulus
          );

          const imagePromises = currentSession.current[0].SessionMoves.map(
            (move) => fetchBlobUrl(move.imageUrl, 'image')
          );

          const resolvedImages = await Promise.all(imagePromises);

          const sequenceOfPlays = currentSession.current[0].SessionMoves.map(
            (move) => move.moveNum
          );

          const playsFromDb = await loadPlaysFromDb();

          infoSession.current = {
            stimulusTime,
            imageSequences: resolvedImages,
            sequenceOfPlays,
            numberOfPlays: currentSession.current[0].numPlays,
            playsFromDb,
          };

          getMinioVideo();
      }
      } catch (error) {
        console.error("[initSession] Error inicializando sesión:", error);
        setVideoSession(PLACEHOLDER_VIDEO);
      }
    };

    initSession();
    return () => {
      clearTimeout(loadingTimeout);
      revokeBlobUrls();
    };
  }, []);


  const uploadVideoToMinio = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await CrudApi.post("minio/upload-video", formData);

    return response.data.path;
  };

  const uploadImageToMinio = async (file) => {
    const formData = new FormData();
    formData.append("file", file);

    const response = await CrudApi.post("minio/upload-image", formData);

    return response.data.path;
  };

  const saveCurrentSession = async () => {
    setSaveStatus("saving");

    const currentDate = new Date();
    const dateStr = `${currentDate.getFullYear()}-${(currentDate.getMonth() + 1).toString().padStart(2, '0')}-${currentDate.getDate().toString().padStart(2, '0')}`;
    const timeStr = `${currentDate.getHours().toString().padStart(2, '0')}_${currentDate.getMinutes().toString().padStart(2, '0')}_${currentDate.getSeconds().toString().padStart(2, '0')}`;

    const video = new File(
      [videoCurrentSession.current],
      `${userContext.userId}/${dateStr}/${timeStr}-player${infoSession.current.playerSelected}.mp4`,
      { type: "video/webm" }
    );

    const images = infoSession.current.imageSequences.map((image) => {
      const byteCharacters = atob(image.split(",")[1]);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      return new File(
        [byteArray],
        `${userContext.userId}/${dateStr}/${timeStr}/player${infoSession.current.playerSelected}`,
        { type: "image/jpeg" }
      );
    });

    try {
      const videoPath = await uploadVideoToMinio(video);

      const imagesPaths = await Promise.all(
        images.map(async (img, index) => {
          return await uploadImageToMinio(new File([img], `${img.name}-play${index}.jpg`, { type: img.type }));
        })
      );

      const sessionData = {
        userId: userContext.userId,
        playerId: parseInt(infoSession.current.playerSelected),
        timestamp: currentDate.toISOString(),
        duration: 0,
        numPlays: infoSession.current.numberOfPlays,
        seed: infoSession.current.seed,
        sessionType: infoSession.current.typeOfSession,
        timeBetweenPlays: infoSession.current.secondsToNextPlay,
        transitionTime: infoSession.current.secondsForPlayTransition,
        videoURL: videoPath,
        numDistractors: infoSession.current.numOfDistractors,
        fps: currentFPS.current,
        calibration: currentCalibration.current,
      };

      const sessionAnalyticData = {
        complete: 0,
        correctPercentage: computeCorrectPercentage(tableData),
        motorMean: computeAverage(tableData, "motor"),
        motorSd: computeStandardDeviation(tableData, "motor"),
        motorTotal: computeTotal(tableData, "motor"),
        responseMean: computeAverage(tableData, "cognitiveMotor"),
        responseSd: computeStandardDeviation(tableData, "cognitiveMotor"),
        responseTotal: computeTotal(tableData, "cognitiveMotor"),
        visuMotorMean: computeAverage(tableData, "visuMotor"),
        visuMotorSd: computeStandardDeviation(tableData, "visuMotor"),
        visuMotorTotal: computeTotal(tableData, "visuMotor"),
        wrongPercentage: computeErrorPercentage(tableData),
      };

      const sessionMovesData = serializeMoves(tableData).map((row, index) => ({
        moveNum: row.playID,
        arrival: row.arrival,
        cognitiveMotor: row.cognitiveMotor,
        error: row.error,
        imageUrl: imagesPaths[index],
        motor: row.motor,
        presentedMs: row.visuMotor,
        stimulus: row.estimulo,
        decisionMaking: row.decisionMaking,
      }));

      await CrudApi.post("sessions", sessionData).then(async (res) => {
        await CrudApi.post(`sessionAnalytics`, { ...sessionAnalyticData, sessionId: res.data.sessionId });

        sessionMovesData.forEach(async (move) => {
          await CrudApi.post(`sessionMoves`, { ...move, sessionId: res.data.sessionId });
        });

        setSaveStatus("saved");
        isSaveCurrentSession.current = true;
      }).catch((error) => { console.error("Error creando sesión:", error); });;

    } catch (err) {
      console.error("Error al guardar sesión:", err);
      Swal.fire("Error", "No se pudo guardar la sesión. Revisa la conexión.", "error");
      setSaveStatus("error");
    }
  };

  const SaveAnalizeSession = async () => {
    if (session === "current" && !isSaveCurrentSession.current) {
      saveCurrentSession();
    } else {
      const dataAnalytic = {
        sessionId: currentSession.current[0].sessionId,
        complete: computeSessionCompleteness(tableData),
        correctPercentage: computeCorrectPercentage(tableData),
        motorMean: computeAverage(tableData, "motor"),
        motorSd: computeStandardDeviation(tableData, "motor"),
        motorTotal: computeTotal(tableData, "motor"),
        responseMean: computeAverage(tableData, "cognitiveMotor"),
        responseSd: computeStandardDeviation(tableData, "cognitiveMotor"),
        responseTotal: computeTotal(tableData, "cognitiveMotor"),
        visuMotorMean: computeAverage(tableData, "visuMotor"),
        visuMotorSd: computeStandardDeviation(tableData, "visuMotor"),
        visuMotorTotal: computeTotal(tableData, "visuMotor"),
        wrongPercentage: computeErrorPercentage(tableData),
      };
      setSaveStatus("saving");

      await CrudApi.update(
        `sessionAnalytics/${currentSession.current[0].SessionAnalytics[0].sessionAnalyticId}`,
        dataAnalytic
      ).then(() => { }).catch((error) => { console.error("Error creando sesión:", error); });
      const dataMoves = serializeMoves(tableData).map((row) => ({
        sessionId: currentSession.current[0].sessionId,
        moveNum: row.playID,
        arrival: row.arrival,
        cognitiveMotor: row.cognitiveMotor,
        error: row.error,
        motor: row.motor,
        presentedMs: row.visuMotor,
        stimulus: row.estimulo,
        decisionMaking: row.decisionMaking,
      }));
      currentSession.current[0].SessionMoves.forEach((move, index) => {
        CrudApi.update(
          `sessionMoves/${move.sessionMovesId}`,
          dataMoves[index]
        ).then(() => {
          setSaveStatus("saved");
        }).catch((error) => { console.error("Error creando sesión:", error); });;
      });
    }
  };

  useEffect(() => {
    if (videoRefs.current.length) {
      if (currentFrame > 0) {
        const currentTime = Math.round((currentFrame / FPS.current) * 1000);
        const stimulusTime = infoSession.current?.stimulusTime;
        let newStimulus = currentStimulus;

        if (stimulusTime) {
          for (let i = 0; i < stimulusTime.length; i++) {
            if (currentTime >= stimulusTime[i]) {
              newStimulus = i;
            } else {
              break;
            }
          }
          setCurrentStimulus(newStimulus);
          const imageSequences = infoSession.current?.imageSequences;
          if (imageSequences?.length) {
            setImagePlay({
              currentPlay: imageSequences[newStimulus],
              prevPlay: imageSequences[Math.max(0, newStimulus - 1)],
            });
          }
        }

        if (videoState === "Play") {
          safeSeekTo(videoRefs.current[0], (currentFrame - 2) / FPS.current, "seconds");
          safeSeekTo(videoRefs.current[1], (currentFrame - 1) / FPS.current, "seconds");
          safeSeekTo(videoRefs.current[2], currentFrame / FPS.current, "seconds");
          safeSeekTo(videoRefs.current[3], (currentFrame + 1) / FPS.current, "seconds");
          safeSeekTo(videoRefs.current[4], (currentFrame + 2) / FPS.current, "seconds");
        }
      } else {
        safeSeekTo(videoRefs.current[0], 0);
        safeSeekTo(videoRefs.current[1], 0);
        safeSeekTo(videoRefs.current[2], 0);
        safeSeekTo(videoRefs.current[3], 0);
        safeSeekTo(videoRefs.current[4], 0);
        setCurrentStimulus(0);
        const imageSequences = infoSession.current?.imageSequences;
        if (imageSequences?.length) {
          setImagePlay({
            currentPlay: imageSequences[0],
            prevPlay: imageSequences[0],
          });
        }
      }
    }
  }, [currentFrame]);

  const handleRowClick = (index, playID) => {
    if (selectedRowIndex === index) {
      selectedPlayID.current = null;
      setSelectedRowIndex(null);
    } else {
      setSelectedRowIndex(index);
      selectedPlayID.current = playID;
    }
  };

  const AddDecisionMakingMark = () => {
    if (selectedRowIndex == null) return;
    const newDecisionMakingValue = Math.round(
      (currentFrame / FPS.current) * 1000
    );
    const existingDecisionMakingValue = parseInt(
      tableData[selectedRowIndex].decisionMaking,
      10
    );
    if (existingDecisionMakingValue !== 0) {
      Swal.fire({
        title: translate("valor_existente"),
        text: translate("valor_decision"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: translate("si_reemplazar"),
        cancelButtonText: translate("cancelar"),
      }).then((result) => {
        if (result.isConfirmed) {
          setTableData((prev) =>
            applyDecisionMark(prev, selectedRowIndex, newDecisionMakingValue)
          );
        }
      });
    } else {
      setTableData((prev) =>
        applyDecisionMark(prev, selectedRowIndex, newDecisionMakingValue)
      );
    }
  };

  const AddArrivalMark = () => {
    if (selectedRowIndex == null) return;
    const newArrivalValue = Math.round((currentFrame / FPS.current) * 1000);
    const existingArrivalValue = parseInt(
      tableData[selectedRowIndex].arrival,
      10
    );
    if (existingArrivalValue !== 0) {
      Swal.fire({
        title: translate("valor_existente"),
        text: translate("valor_arrival"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: translate("si_reemplazar"),
        cancelButtonText: translate("cancelar"),
      }).then((result) => {
        if (result.isConfirmed) {
          updateArrivalValue(newArrivalValue);
        }
      });
    } else {
      updateArrivalValue(newArrivalValue);
    }

    function updateArrivalValue(newArrivalValue) {
      const updatedTableData = applyArrivalMark(
        tableData,
        selectedRowIndex,
        newArrivalValue
      );
      setTableData(updatedTableData);

      if (selectedRowIndex + 1 < tableData.length) {
        setSelectedRowIndex(selectedRowIndex + 1);
      } else {
        setSelectedRowIndex(null);
      }
    }
  };

  useEffect(() => {
    if (currentSession.current !== null && tableData.length > 0) {
      setMetrics((prev) =>
        computeMetrics(tableData, {
          isCurrentSession: session === "current",
          storedAnalytics:
            currentSession.current?.[0]?.SessionAnalytics?.[0] || null,
          useStored: prev === null,
        })
      );
    }
  }, [tableData]);

  const handleSkipBack = () => {
    if (currentStimulus - 1 > 0) {
      let toFrame = Math.round(
        (infoSession.current.stimulusTime[currentStimulus - 1] / 1000) *
        FPS.current
      );
      safeSeekTo(
        videoRefs.current[2],
        infoSession.current.stimulusTime[currentStimulus - 1] / 1000
      );
      if (
        Math.round((toFrame / FPS.current) * 1000) <
        infoSession.current.stimulusTime[currentStimulus - 1]
      ) {
        setSelectedRowIndex(currentStimulus - 1);
        setCurrentFrame(
          Math.round(
            safeGetCurrentTime(videoRefs.current[2]) * FPS.current
          ) + 1
        );
      } else {
        setSelectedRowIndex(currentStimulus - 1);
        setCurrentFrame(
          Math.round(
            safeGetCurrentTime(videoRefs.current[2]) * FPS.current
          )
        );
      }
    } else {
      setSelectedRowIndex(0);
      safeSeekTo(videoRefs.current[2], 0);
      setCurrentFrame(
        Math.round(
          safeGetCurrentTime(videoRefs.current[2]) * FPS.current
        )
      );
    }
  };

  const handleFrameBack = () => {
    if (videoState === "Pause") {
      setVideoState("Play");
    }
    if (currentFrame - 1 > 0) {
      setCurrentFrame(currentFrame - 1);
    } else {
      setCurrentFrame(0);
    }
  };

  const handlePlayPauseToggle = () => {
    if (videoState === "Play") {
      setVideoState("Pause");
      setPlayingVideo(true);
    } else {
      setVideoState("Play");
      setPlayingVideo(false);
    }
  };

  const handleFrameForward = () => {
    if (videoState === "Pause") {
      setVideoState("Play");
    }
    if (currentFrame + 1 < Math.round(videoDuration * FPS.current)) {
      setCurrentFrame(currentFrame + 1);
    } else {
      setCurrentFrame(Math.round(videoDuration * FPS.current));
    }
  };

  const handleSkipForward = () => {
    if (currentStimulus + 1 < infoSession.current.stimulusTime.length - 1) {
      let toFrame = Math.round(
        (infoSession.current.stimulusTime[currentStimulus + 1] / 1000) *
        FPS.current
      );
      safeSeekTo(
        videoRefs.current[2],
        infoSession.current.stimulusTime[currentStimulus + 1] / 1000
      );
      if (
        Math.round((toFrame / FPS.current) * 1000) <
        infoSession.current.stimulusTime[currentStimulus + 1]
      ) {
        setSelectedRowIndex(currentStimulus + 1);
        setCurrentFrame(
          Math.round(
            safeGetCurrentTime(videoRefs.current[2]) * FPS.current
          ) + 1
        );
      } else {
        setSelectedRowIndex(currentStimulus + 1);
        setCurrentFrame(
          Math.round(
            safeGetCurrentTime(videoRefs.current[2]) * FPS.current
          )
        );
      }
    } else {
      safeSeekTo(
        videoRefs.current[2],
        infoSession.current.stimulusTime[
        infoSession.current.stimulusTime.length - 1
        ] / 1000
      );
      setSelectedRowIndex(
        infoSession.current.stimulusTime.length - 1
      );
      setCurrentFrame(
        Math.round(
          safeGetCurrentTime(videoRefs.current[2]) * FPS.current
        )
      );
    }
  };

  const handleSeek = (seconds) => {
    setVideoState("Play");
    setPlayingVideo(false);
    setCurrentFrame(Math.round(seconds * FPS.current));
  };

  const handleDuration = (duration) => {
    if (typeof duration === "number" && Number.isFinite(duration) && duration > 0) {
      setVideoDuration(duration);
    }
  };

  const handleCellClick = (index, columnId, row) => {
    if (columnId === "stimulus") {
      const estimulusFrame = Math.round(
        (parseInt(row.estimulo) * FPS.current) / 1000
      );
      const frameTime = Math.round((estimulusFrame / FPS.current) * 1000);
      if (frameTime < row.estimulo) {
        setCurrentFrame(estimulusFrame + 1);
      } else {
        setCurrentFrame(estimulusFrame);
      }
      if (selectedRowIndex !== index) {
        handleRowClick(index, row.playID);
      }
    } else if (columnId === "decisionMaking") {
      const value = parseInt(row.decisionMaking, 10);
      if (value > 0) {
        setCurrentFrame(Math.round((value * FPS.current) / 1000));
      }
      if (selectedRowIndex !== index) {
        handleRowClick(index, row.playID);
      }
    } else if (columnId === "arrival") {
      const value = parseInt(row.arrival, 10);
      if (value > 0) {
        setCurrentFrame(Math.round((value * FPS.current) / 1000));
      }
      if (selectedRowIndex !== index) {
        handleRowClick(index, row.playID);
      }
    } else {
      handleRowClick(index, row.playID);
    }
  };

  const clearAnalysis = () => {
    Swal.fire({
      title: translate("estas_seguro"),
      text: translate("borrar_valores"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: translate("si_borrar"),
      cancelButtonText: translate("cancelar"),
    }).then((result) => {
      if (result.isConfirmed) {
        setTableData((prev) => resetRows(prev));
        Swal.fire(
          translate("borrado"),
          translate("valores_borrados"),
          "success"
        );
      }
    });
  };

  const autoAnalysis = async () => {
    // verificar si hay valores de arrival y decision making en por lo menos una fila
    const hasValues = tableData.some(
      (row) =>
        parseInt(row.decisionMaking, 10) !== 0 ||
        parseInt(row.arrival, 10) !== 0
    );
    if (hasValues) {
      Swal.fire({
        title: translate("estas_seguro"),
        text: translate("sobreescribir_valores"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: translate("si_sobreescribir"),
        cancelButtonText: translate("cancelar"),
      }).then(async (result) => {
        if (result.isConfirmed) {
          await processingSteps();
        }
      });
    } else {
      await processingSteps();
    }
  };

  const processingSteps = async () => {
    //ventana de confirmacion de procesamiento
    Swal.fire({
      title: translate("seguro_procesar"),
      text: translate("tardar_minutos"),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: translate("si_procesar"),
      cancelButtonText: translate("cancelar"),
    }).then(async (result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: translate("procesando"),
          text: translate("tardara_minutos"),
          allowOutsideClick: false,
          allowEscapeKey: false,
          allowEnterKey: false,
          showConfirmButton: false,
          showCancelButton: false,
          showCloseButton: false,
          showLoaderOnConfirm: true,
        });
        setProcessing({ value: 0, message: "Procesando..." });
        let dataAutoAnalysis = {};
        if (session === "current") {
        } else {
          let marks = currentSession.current[0].SessionMoves.map((move) => ({
            mark_correct: move.correctResponse,
            frame: Math.round(
              (move.stimulus * currentSession.current[0].fps) / 1000
            ),
          }));

          // Construir URLs usando las rutas de la API
          const apiBaseUrl = process.env.REACT_APP_APIMCP || 'https://www.spt-group.cl/api';

          dataAutoAnalysis = {
            contourjson: JSON.stringify(currentSession.current[0].calibration),
            videoUrl: `${apiBaseUrl}/public/minio/video/${currentSession.current[0].videoURL}`,
            imageUrl: `${apiBaseUrl}/public/minio/image/${currentSession.current[0].imageCalibration}`,
            jsonString: JSON.stringify(marks),
            frameRate: currentSession.current[0].fps,
          };

          await axios
            .post(`${urlVision}autoAnalysis`, dataAutoAnalysis, {
              timeout: 600000,
              maxBodyLength: Infinity,
              maxContentLength: Infinity,
            })
            .then((response) => {
              Swal.close();
              setProcessing({ value: 1, message: "Procesar Pasos" });
              refillAnalysisTable(JSON.parse(response.data.response.output));
            })
            .catch((error) => {
              Swal.close();
              setProcessing({
                value: 2,
                message: "Error procesando",
              });
              setTimeout(() => {
                setProcessing({ value: 1, message: "Procesar Pasos" });
              }, 3000);
            });
        }
      }
    });
  };

  const refillAnalysisTable = (data) => {
    const updatedData = tableData.map((row, index) => {
      if (index < data.length) {
        let error = false;
        let arrival = 0;
        let decisionMaking = 0;
        let visuMotor = 0;
        let motor = 0;
        let cognitiveMotor = 0;
        const newRow = data[index];
        if (newRow.arrival_frame > Math.round(videoDuration * FPS.current)) {
          newRow.arrival_frame = tableData[index].arrival;
        }
        if (newRow.takeoff_frame > Math.round(videoDuration * FPS.current)) {
          newRow.takeoff_frame = tableData[index].decisionMaking;
        }
        error = newRow.error;
        arrival = Math.round(
          (parseInt(newRow.arrival_frame) / FPS.current) * 1000
        );
        decisionMaking = Math.round(
          (parseInt(newRow.takeoff_frame) / FPS.current) * 1000
        );
        visuMotor = decisionMaking - parseInt(row.estimulo);
        motor = arrival - decisionMaking;
        cognitiveMotor = visuMotor + motor;

        return {
          ...row,
          error: error,
          arrival: arrival,
          decisionMaking: decisionMaking,
          visuMotor: visuMotor,
          motor: motor,
          cognitiveMotor: cognitiveMotor,
          autoComplete: true,
        };
      } else {
        return row;
      }
    });
    setTableData(updatedData);

  };

  const sessionMetadata = {
    tipoPrueba:
      session !== "current"
        ? currentSession.current?.[0]?.sessionType
          ? showSessionType(currentSession.current[0].sessionType)
          : ""
        : infoSession.current?.typeOfSession
          ? showSessionType(infoSession.current.typeOfSession)
          : "",
    group:
      session !== "current"
        ? (currentSession.current?.[1]?.SportGroup ?? "")
        : (currentPlayer.current?.SportGroup ?? ""),
    name:
      session !== "current"
        ? (currentSession.current?.[1]?.Name ?? "")
        : (currentPlayer.current?.Name ?? ""),
    surname:
      session !== "current"
        ? (currentSession.current?.[1]?.Surname ?? "")
        : (currentPlayer.current?.Surname ?? ""),
    date:
      session !== "current"
        ? currentSession.current?.[0]?.timestamp
          ? currentSession.current[0].timestamp.split("T")[0]
          : ""
        : new Date().toISOString().split("T")[0],
  };

  if (!infoSession?.current) {
    return (
      <div className="AnalizeSessionContainer h-screen w-screen max-h-screen overflow-hidden bg-base flex flex-col items-center justify-center gap-3">
        <Spinner size="md" />
        <span className="text-sm font-medium text-secondary">
          {translate("cargando")}
        </span>
      </div>
    );
  }
  return (
    <div className="AnalizeSessionContainer h-screen w-screen max-h-screen overflow-hidden flex flex-col">
      <SessionHeader
        className="flex-none shrink-0"
        onBack={() => {
          if (session === "current") {
            navigate("/football-session");
          } else {
            infoSession.current = null;
            navigate("/other-sessions");
          }
        }}
        metadata={sessionMetadata}
        fps={FPS.current}
        frames={Math.round(videoDuration * FPS.current)}
        currentFrame={currentFrame}
        currentTimeMs={Math.round((currentFrame / FPS.current) * 1000)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        translate={translate}
      />
      <AnalysisWorkspace
        className="flex-1 min-h-0"
        viewMode={viewMode}
        video={{
          url: videoSession,
          playing: playingVideo,
          loading,
          currentFrame,
          fps: FPS.current,
          videoDuration,
          currentTime: videoDuration ? currentFrame / FPS.current : 0,
        }}
        videoRefs={videoRefs}
        onSeek={handleSeek}
        onDuration={handleDuration}
        onProgress={(event) => {
          if (!event) return;
          const playedSeconds = event.playedSeconds ?? 0;
          setCurrentFrame(Math.round(playedSeconds * FPS.current));
        }}
        onEnded={() => {
          setPlayingVideo(false);
          setVideoState("Play");
        }}
        onReady={(playerRef) => {
          setLoading(false);
          if (playerRef) {
            try {
              const d = playerRef.getDuration?.();
              if (typeof d === "number" && Number.isFinite(d) && d > 0) {
                setVideoDuration(d);
              }
            } catch (_) {}
          }
        }}
        playback={{
          isPlaying: playingVideo,
          onSkipBackDouble: handleSkipBack,
          onFrameBack: handleFrameBack,
          onPlayPauseToggle: handlePlayPauseToggle,
          onFrameForward: handleFrameForward,
          onSkipForwardDouble: handleSkipForward,
        }}
        frameLabel={translate("frame")}
        timeMsLabel={translate("tiempo_ms_")}
        table={{ rows: tableData, selectedRowIndex }}
        onRowSelect={(index, playID) => handleRowClick(index, playID)}
        onCellClick={handleCellClick}
        onCheckboxChange={handleCheckboxChange}
        currentPlay={{
          prevImage: imagePlay.prevPlay,
          currentImage: imagePlay.currentPlay,
          prevTimeMs:
            infoSession.current?.stimulusTime?.[currentStimulus - 1] || 0,
          currentTimeMs:
            infoSession.current?.stimulusTime?.[currentStimulus] || 0,
          prevPlayNumber: infoSession.current?.stimulusTime?.[
            currentStimulus - 1
          ]
            ? currentStimulus
            : 1,
          currentPlayNumber: currentStimulus + 1,
          prevPlay: findPlayBySequence(
            infoSession.current?.sequenceOfPlays?.[currentStimulus - 1]
          ),
          currentPlay: findPlayBySequence(
            infoSession.current?.sequenceOfPlays?.[currentStimulus]
          ),
        }}
        actions={{
          onDecisionMaking: AddDecisionMakingMark,
          onArrival: AddArrivalMark,
          onAutoAnalysis: autoAnalysis,
          onSave: SaveAnalizeSession,
          onClear: clearAnalysis,
          marksDisabled: selectedRowIndex == null,
          saveStatus,
          autoAnalysisDisabled:
            (session === "current" && currentCalibration.current === null) ||
            !currentSession.current?.[0]?.calibration ||
            processing.value !== 1,
          processing,
        }}
        metrics={metrics}
        translate={translate}
      />
    </div>
  );
};

export default AnalizeSession;
